import axios, { AxiosInstance } from 'axios';
import { Conversation, Message, LoginResponse, SendMessageRequest, EditMessageRequest } from '@/types';

class ApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: (import.meta.env.VITE_API_URL || '/api').replace(/\/$/, ''),
    });

    this.client.interceptors.request.use((config) => {
      const token = localStorage.getItem('supportToken');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        // Don't redirect if it's a 401 on the login or shared chat endpoint
        const isPublicEndpoint = error.config.url.includes('/support/login') || error.config.url.includes('/support/shared/');
        
        if (error.response?.status === 401 && !isPublicEndpoint) {
          localStorage.removeItem('supportToken');
          window.location.href = '/login';
        }
        // Also handle 403 Access Denied
        if (error.response?.status === 403) {
          console.error('Access denied: Admin privileges required');
        }
        return Promise.reject(error);
      }
    );
  }

  async login(password: string): Promise<LoginResponse> {
    const response = await this.client.post<LoginResponse>('/support/login', { password });
    return response.data;
  }

  async getConversations(chatType?: 'SUPPORT' | 'SPECIALIST'): Promise<Conversation[]> {
    const response = await this.client.get<Conversation[]>('/support/conversations', {
      params: { chatType }
    });
    return response.data;
  }

  async getMessages(conversationId: string, since?: string): Promise<Message[]> {
    const url = since ? `/support/messages/${conversationId}?since=${encodeURIComponent(since)}` : `/support/messages/${conversationId}`;
    const response = await this.client.get<Message[]>(url);
    return response.data;
  }

  async shareConversation(conversationId: string): Promise<{ shareId: string }> {
    const response = await this.client.post<{ shareId: string }>(`/support/share/${conversationId}`);
    return response.data;
  }

  async getSharedConversation(shareId: string): Promise<{ conversation: Conversation; messages: Message[] }> {
    const response = await this.client.get<{ conversation: Conversation; messages: Message[] } | { data: { conversation: Conversation; messages: Message[] } }>(`/support/shared/${shareId}`);
    
    // Some APIs wrap the response in a 'data' property
    const data = (response.data as any)?.data && (response.data as any)?.conversation === undefined
      ? (response.data as any).data
      : response.data;
      
    return data;
  }

  async getUserProfile(userId: string): Promise<{ id: string; fullName: string; email: string; phone?: string; createdAt: string; lastLoginAt?: string; avatar?: string } | null> {
    try {
      const response = await this.client.get(`/support/user/${userId}`);
      return response.data;
    } catch {
      return null;
    }
  }

  async sendMessage(data: SendMessageRequest): Promise<Message> {
    const response = await this.client.post<Message>('/support/send', data);
    return response.data;
  }

  async editMessage(data: EditMessageRequest): Promise<Message> {
    const response = await this.client.post<Message>('/support/edit', data);
    return response.data;
  }

  async deleteMessage(messageId: string): Promise<{ success: boolean }> {
    const response = await this.client.post<{ success: boolean }>('/support/delete', { messageId });
    return response.data;
  }

  async sendImageMessage(formData: FormData): Promise<Message> {
    const response = await this.client.post<Message>('/support/send-image', formData);
    return response.data;
  }
}

export const api = new ApiClient();
